import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRoleUsers } from '../hooks/useRoleUsers';
import AppLayout from '../components/AppLayout';
import ProductTable from '../components/ProductTable';
import FilterBar from '../components/FilterBar';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Package, CheckCircle, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { sortSizeEntries } from '../lib/sizeSort';
import { submitWarehouseReceive } from '../lib/warehouseReceive';
import { getWarehouseStockSnapshot } from '../lib/warehouseExport';

const WarehouseDashboard: React.FC = () => {
  const { user } = useAuth();
  const { getUsersByRole } = useRoleUsers();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [filters, setFilters] = useState({
    mainCategory: '',
    subCategory: '',
    department: '',
    priceStatus: '',
    stockStatus: '',
    dateRange: '',
    search: '',
    statusFilter: ''
  });
  const [stockData, setStockData] = useState<{
    sizeQuantities: Record<string, number>;
    location: string;
    physicallyReceived: boolean;
  }>({
    sizeQuantities: {},
    location: '',
    physicallyReceived: false
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/products');
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Warehouse load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Status filtresi (kart tıklamaları için)
    if (filters.statusFilter) {
      if (filters.statusFilter === 'nebim') {
        // Nebim kartı hem production_entry hem de nebim_entry statülerini gösterir
        filtered = filtered.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry');
      } else if (filters.statusFilter === 'publish_pending') {
        // TODO: migration sonrası legacy OR kaldırılacak
        filtered = filtered.filter(p =>
          p.status === 'publish_pending' ||
          (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
        );
      } else if (filters.statusFilter === 'image_upload') {
        filtered = filtered.filter(
          p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
        );
      } else if (filters.statusFilter === 'published') {
        filtered = filtered.filter(p => p.status === 'published' && p.isActive !== false);
      } else {
        filtered = filtered.filter(p => p.status === filters.statusFilter);
      }
    }

    // Kategori filtreleri
    if (filters.mainCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.mainCategory === filters.mainCategory);
    }
    if (filters.subCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.subCategory === filters.subCategory);
    }

    // Arama filtresi
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(p => {
        const productName = (p.productionInfo?.productName || '').toLowerCase();
        const sku = (p.nebimInfo?.sku || '').toLowerCase();
        const manufacturer = (p.productionInfo?.manufacturer || '').toLowerCase();
        
        return productName.includes(search) || 
               sku.includes(search) || 
               manufacturer.includes(search);
      });
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReceive = async (productId: string) => {
    const product = products.find(p => p._id === productId);
    if (product) {
      setSelectedProduct(product);
      // Beden dağılımından başlangıç değerlerini al
      const initialQuantities: Record<string, number> = {};
      if (product.productionInfo?.sizeDistribution) {
        sortSizeEntries(product.productionInfo.sizeDistribution).forEach(([size]) => {
          initialQuantities[size] = 0;
        });
      }
      setStockData({
        sizeQuantities: initialQuantities,
        location: '',
        physicallyReceived: false
      });
      setShowModal(true);
    }
  };
  
  const handleStockSubmit = async () => {
    if (!selectedProduct) return;
    
    // Toplam stok adedini hesapla
    const totalQuantity = Object.values(stockData.sizeQuantities).reduce((sum, qty) => sum + qty, 0);
    
    if (totalQuantity === 0) {
      toast.error('En az bir beden için stok girmelisiniz!');
      return;
    }
    
    if (!stockData.location) {
      toast.error('Depo lokasyonu girmelisiniz!');
      return;
    }

    if (!stockData.physicallyReceived) {
      toast.error('Ürünün fiziki olarak teslim alındığını onaylayın!');
      return;
    }
    
    try {
      await submitWarehouseReceive(selectedProduct._id, {
        sizeQuantities: stockData.sizeQuantities,
        stockQuantity: totalQuantity,
        location: stockData.location,
        physicallyReceived: stockData.physicallyReceived
      });
      
      toast.success('Ürün depoya kabul edildi ve stok girişine alındı!');
      setShowModal(false);
      setSelectedProduct(null);
      setStockData({ sizeQuantities: {}, location: '', physicallyReceived: false });
      loadProducts();
    } catch (error) {
      toast.error('İşlem başarısız!');
      console.error('Receive error:', error);
    }
  };

  const handleReject = async (productId: string, targetStep: string, reason: string) => {
    try {
      const response = await axios.post(`/products/${productId}/reject`, {
        targetStep,
        reason,
        rejectedBy: user?.id
      });
      
      if (response.data) {
        toast.success('Ürün başarıyla geri yollandı');
        loadProducts();
      }
    } catch (error) {
      toast.error('Geri yollama işlemi başarısız!');
      console.error('Reject error:', error);
      throw error;
    }
  };

  const handleClosureRequest = async (productId: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/closure-request`, { reason });
      toast.success('Kapatma talebi gönderildi!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Talep gönderilemedi!');
    }
  };

  const handleExcelExport = async () => {
    try {
      const headers = [
        'ID',
        'Ürün Adı',
        'Üretici',
        'SKU',
        'Bedenler',
        'Stok Adetleri',
        'Lokasyon',
        'Durum',
        'Kategori',
        'Teslim Tarihi'
      ];
      
      const rows = filteredProducts.map(p => {
        const sizes = p.productionInfo?.sizeDistribution 
          ? sortSizeEntries(p.productionInfo.sizeDistribution).map(([size, qty]) => `${size}:${qty}`).join(', ')
          : '';
        
        const stockSnapshot = getWarehouseStockSnapshot(p);
        const stockQuantities = stockSnapshot.sizeQuantities
          ? sortSizeEntries(stockSnapshot.sizeQuantities).map(([size, qty]) => `${size}:${qty}`).join(', ')
          : '';
        
        const category = [
          p.categoryInfo?.mainCategory,
          p.categoryInfo?.subCategory,
          p.categoryInfo?.detailCategory
        ].filter(Boolean).join(' > ');
        
        return [
          p._id,
          p.productionInfo?.productName || '',
          p.productionInfo?.manufacturer || '',
          p.nebimInfo?.sku || '',
          sizes,
          stockQuantities,
          stockSnapshot.location || '',
          p.status,
          category || '',
          stockSnapshot.receivedDate ? new Date(stockSnapshot.receivedDate).toLocaleDateString('tr-TR') : ''
        ];
      });
      
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join('\t'),
        ...rows.map(row => row.join('\t'))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `depo_urunler_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Excel dosyası indirildi!');
    } catch (error) {
      toast.error('Excel export başarısız!');
    }
  };

  const stats = {
    draft: products.filter(p => p.status === 'draft').length,
    nebim: products.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry').length,
    barcode_sent: products.filter(p => p.status === 'barcode_sent').length,
    stock_entry: products.filter(p => p.status === 'stock_entry').length,
    api_transfer: products.filter(p => p.status === 'api_transfer').length,
    content_entry: products.filter(p => p.status === 'content_entry').length,
    category_setup: products.filter(p => p.status === 'category_setup').length,
    image_upload: products.filter(
      p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
    ).length,
    publish_pending: products.filter(p =>
      p.status === 'publish_pending' ||
      (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
    ).length,
    published: products.filter(p => p.status === 'published' && p.isActive !== false).length,
    total: products.length
  };

  // Kart tıklanınca filtreleme yap
  const handleCardClick = (status: string) => {
    if (filters.statusFilter === status) {
      // Aynı karta tekrar tıklanırsa filtreyi kaldır
      setFilters(prev => ({ ...prev, statusFilter: '' }));
    } else {
      // Yeni bir karta tıklanırsa o statüye filtrele
      setFilters(prev => ({ ...prev, statusFilter: status }));
    }
  };

  return (
    <AppLayout
      title="Depo Paneli"
      subtitle={`Stok Kabul & Yönetim - ${getUsersByRole('warehouse') || 'Depo'}`}
    >
      {/* Stock Entry Modal */}
      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-primary/5 to-primary/10">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Depo Stok Kabulü</h2>
                <p className="text-sm text-gray-600 mt-1">Ürün stoklarını beden bazlı olarak kaydedin</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-gray-600 h-8 w-8 p-0"
                onClick={() => {
                  setShowModal(false);
                  setSelectedProduct(null);
                  setStockData({ sizeQuantities: {}, location: '', physicallyReceived: false });
                }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Product Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {selectedProduct.productionInfo?.productName}
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Üretici:</span>
                    <span className="ml-2 font-medium">{selectedProduct.productionInfo?.manufacturer}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">SKU:</span>
                    <span className="ml-2 font-medium">{selectedProduct.nebimInfo?.sku || '-'}</span>
                  </div>
                </div>
              </div>
              
              {/* Size-based Stock Input */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <h4 className="font-medium text-gray-900">Beden Bazlı Stok Girişi</h4>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {sortSizeEntries(stockData.sizeQuantities).map(([size, quantity]) => (
                    <div key={size} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-primary/50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="font-semibold">
                          {size}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          Sipariş: {selectedProduct.productionInfo?.sizeDistribution?.[size] || 0}
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="number"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors text-center font-mono text-lg"
                          value={quantity || ''}
                          onChange={(e) => setStockData({
                            ...stockData,
                            sizeQuantities: {
                              ...stockData.sizeQuantities,
                              [size]: parseInt(e.target.value) || 0
                            }
                          })}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Toplam Gelen: {Object.values(stockData.sizeQuantities).reduce((sum, qty) => sum + qty, 0)} adet
                      </p>
                      <p className="text-xs text-blue-700">
                        Toplam Sipariş: {selectedProduct.productionInfo?.sizeDistribution ? 
                          Object.values(selectedProduct.productionInfo.sizeDistribution).reduce((sum, qty) => sum + qty, 0) : 0
                        } adet
                      </p>
                    </div>
                    <div className="text-right">
                      {Object.values(stockData.sizeQuantities).reduce((sum, qty) => sum + qty, 0) > 0 && (
                        <div className="text-lg font-bold text-blue-900">
                          %{Math.round((Object.values(stockData.sizeQuantities).reduce((sum, qty) => sum + qty, 0) / 
                            (selectedProduct.productionInfo?.sizeDistribution ? 
                              Object.values(selectedProduct.productionInfo.sizeDistribution).reduce((sum, qty) => sum + qty, 0) : 1
                            )) * 100)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Location Input */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <label className="font-medium text-gray-900">Depo Lokasyonu</label>
                </div>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  value={stockData.location}
                  onChange={(e) => setStockData({ ...stockData, location: e.target.value })}
                  placeholder="Örn: A1-B2, Raf-15, Koridor-C"
                />
              </div>

              {/* Fiziki Teslim Onayı */}
              <div className="border-t pt-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={stockData.physicallyReceived}
                    onChange={(e) => setStockData({...stockData, physicallyReceived: e.target.checked})}
                    className="w-4 h-4 text-primary border-input rounded focus:ring-ring"
                  />
                  <span className="text-sm font-medium">Ürün fiziki olarak teslim alındı</span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-6 border-t bg-gray-50">
              <Button
                onClick={handleStockSubmit}
                disabled={!stockData.location || !stockData.physicallyReceived || Object.values(stockData.sizeQuantities).every(q => q === 0)}
                className="flex-1 py-3 text-base font-medium"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Stok Girişi Yap & ERP'ye Gönder
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  setSelectedProduct(null);
                  setStockData({ sizeQuantities: {}, location: '', physicallyReceived: false });
                }}
                className="px-8 py-3 text-base"
              >
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="container mx-auto py-8 space-y-8 px-4">
        {/* Gösterge Paneli */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-green-900">Görevleriniz</h3>
              <p className="text-sm text-green-700 mt-1">
                Üreticiden gelen ürünleri teslim alın, sayım yapın ve Nebim sistemine stok girişini yapın. 
                Fiziki teslim onayı olmadan işlem tamamlanamaz.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards - Hiyerarşi Adımları */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* 1. Ürün Bilgileri Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'draft' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('draft')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ürün Girişi</CardTitle>
              <Badge variant="secondary">{stats.draft}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 2. Nebim İşlemleri (SKU + Barkod) */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'nebim' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('nebim')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Nebim İşlemleri</CardTitle>
              <Badge variant="secondary">{stats.nebim}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('nebim') || 'Nebim'} (SKU/Barkod)</p>
            </CardContent>
          </Card>
          
          {/* 3. Üreticiye Gönderildi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'barcode_sent' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('barcode_sent')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Üreticiye Gönderim</CardTitle>
              <Badge variant="secondary">{stats.barcode_sent}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 4. Stok Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'stock_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('stock_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Stok Girişi</CardTitle>
              <Badge variant="secondary">{stats.stock_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('warehouse') || 'Depo'}</p>
            </CardContent>
          </Card>
          
          {/* 5. Ticimaxa Aktarım */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'api_transfer' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('api_transfer')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ticimaxa Aktarım</CardTitle>
              <Badge variant="secondary">{stats.api_transfer}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
          
          {/* 6. İçerik Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'content_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('content_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">İçerik</CardTitle>
              <Badge variant="secondary">{stats.content_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('content') || 'İçerik'}</p>
            </CardContent>
          </Card>
          
          {/* 7. Görsel Girişi */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'image_upload' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('image_upload')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Görsel</CardTitle>
              <Badge variant="secondary">{stats.image_upload}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('visual') || 'Görsel'}</p>
            </CardContent>
          </Card>

          {/* 8. Yayın Bekliyor */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'publish_pending' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('publish_pending')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayın Bekliyor</CardTitle>
              <Badge variant="secondary">{stats.publish_pending}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('publish') || 'Yayın'}</p>
            </CardContent>
          </Card>

          {/* 9. Yayınlandı */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'published' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('published')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayınlanan Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-green-600 text-white">{stats.published}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Toplam Kart - Ayrı satırda */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === '' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tüm Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-blue-600 text-white">{stats.total}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Tüm ürünler (filtreyi temizle)</p>
            </CardContent>
          </Card>
        </div>

        {/* Aktif Filtre Göstergesi */}
        {filters.statusFilter && (
          <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium">Aktif Filtre:</span>
            <Badge variant="outline" className="gap-1">
              {filters.statusFilter === 'draft' && 'Ürün Girişi'}
              {filters.statusFilter === 'nebim' && 'Nebim İşlemleri'}
              {filters.statusFilter === 'barcode_sent' && 'Üreticiye Gönderim'}
              {filters.statusFilter === 'stock_entry' && 'Stok Girişi'}
              {filters.statusFilter === 'api_transfer' && 'Ticimaxa Aktarım'}
              {filters.statusFilter === 'content_entry' && 'İçerik'}
              {filters.statusFilter === 'image_upload' && 'Görsel'}
              {filters.statusFilter === 'publish_pending' && 'Yayın Bekliyor'}
              {filters.statusFilter === 'published' && 'Yayınlandı'}
              <button 
                onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
                className="ml-1 hover:bg-gray-200 rounded-full p-1"
              >
                ✕
              </button>
            </Badge>
          </div>
        )}

        {/* Aktif Filtre Göstergesi */}
        {filters.statusFilter && (
          <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium">Aktif Filtre:</span>
            <Badge variant="outline" className="gap-1">
              {filters.statusFilter === 'waiting' && 'Bekleyen'}
              {filters.statusFilter === 'received' && 'Kabul Edildi'}
              <button 
                onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
                className="ml-1 hover:bg-gray-200 rounded-full p-1"
              >
                ✕
              </button>
            </Badge>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExcelExport} className="gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Excel'e Aktar
            </Button>
          </div>
        </div>
          
        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <FilterBar 
              filters={filters}
              onFilterChange={handleFilterChange}
              products={products}
            />
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Depo ürünü bulunamadı</p>
              </div>
            ) : (
              <ProductTable 
                products={filteredProducts}
                onRefresh={loadProducts}
                onWarehouseAction={handleReceive}
                onReject={handleReject}
                onClosureRequest={handleClosureRequest}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default WarehouseDashboard;
